import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Shield,
  ChevronRight,
  Home,
  LayoutDashboard,
  Menu,
  Moon,
  Sun,
  UserRound,
  X,
} from "lucide-react";

import { getProfile } from "../../lib/profileEngine";
import { useTheme } from "../../lib/theme";
import { isEmbedded } from "../../lib/embedded";

const baseLinks = [
  { to: "/", label: "Home", icon: Home },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
];

const railVariants = {
  collapsed: {
    width: 72,
    transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] },
  },
  expanded: {
    width: 300,
    transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -8 },
  show: (index = 0) => ({
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.28,
      delay: index * 0.04,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const ThemeToggle = ({ theme, toggleTheme, expanded, mobile = false }) => {
  const isDark = theme === "dark";

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className={
        mobile
          ? "flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-base tracking-widest-2 uppercase font-mono text-ink-2 transition-all hover:text-ink hover:bg-white/[0.03]"
          : `group flex items-center rounded-2xl px-3 py-3 text-[16px] tracking-widest-2 uppercase font-mono overflow-hidden transition-all text-ink-2 hover:text-ink hover:bg-white/[0.03] ${
              expanded ? "gap-3 justify-start w-full" : "justify-center"
            }`
      }
    >
      <span className="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border border-line-2 bg-black/20">
        <motion.span
          className="absolute left-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-gold"
          style={{ color: "var(--btn-ink)" }}
          animate={{ x: isDark ? 0 : 16 }}
          transition={{ duration: 0.2 }}
        >
          {isDark ? <Moon className="h-2.5 w-2.5" /> : <Sun className="h-2.5 w-2.5" />}
        </motion.span>
      </span>
      {mobile ? (
        <span>{isDark ? "Dark Mode" : "Light Mode"}</span>
      ) : (
        <AnimatePresence initial={false}>
          {expanded ? (
            <motion.span
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -6 }}
              transition={{ duration: 0.18 }}
              className="whitespace-nowrap"
            >
              {isDark ? "Dark Mode" : "Light Mode"}
            </motion.span>
          ) : null}
        </AnimatePresence>
      )}
    </button>
  );
};

const NavBar = () => {
  const { pathname } = useLocation();
  const profile = getProfile();
  const { theme, toggleTheme } = useTheme();
  const embedded = isEmbedded();
  const [expanded, setExpanded] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [finePointer, setFinePointer] = useState(false);
  const links = profile?.role === "admin"
    ? [...baseLinks, { to: "/admin", label: "Admin", icon: Shield }]
    : baseLinks;

  useEffect(() => {
    const media = window.matchMedia("(pointer: fine)");
    const syncPointer = () => setFinePointer(media.matches);
    syncPointer();

    if (media.addEventListener) {
      media.addEventListener("change", syncPointer);
    } else {
      media.addListener(syncPointer);
    }

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener("change", syncPointer);
      } else {
        media.removeListener(syncPointer);
      }
    };
  }, []);

  const renderLink = (link, index, mobile = false) => {
    const Icon = link.icon;
    const baseClass = mobile
      ? "flex items-center gap-3 rounded-2xl px-4 py-3 text-base tracking-widest-2 uppercase font-mono"
      : `group flex items-center rounded-2xl px-3 py-3 text-[16px] tracking-widest-2 uppercase font-mono overflow-hidden ${
          expanded ? "gap-3 justify-start" : "justify-center"
        }`;

    const label = mobile ? (
      <span>{link.label}</span>
    ) : (
      <AnimatePresence initial={false}>
        {expanded ? (
          <motion.span
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -6 }}
            transition={{ duration: 0.18 }}
            className="whitespace-nowrap"
          >
            {link.label}
          </motion.span>
        ) : null}
      </AnimatePresence>
    );

    return (
      <motion.div key={link.label} custom={index} variants={itemVariants} initial="hidden" animate="show">
        <NavLink
          to={link.to}
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) =>
            `${baseClass} transition-all ${
              isActive
                ? "text-gold bg-white/[0.04] shadow-[inset_0_0_0_1px_rgba(221,167,79,0.22)]"
                : "text-ink-2 hover:text-ink hover:bg-white/[0.03]"
            }`
          }
        >
          <Icon className="h-4 w-4 shrink-0" />
          {label}
        </NavLink>
      </motion.div>
    );
  };

  return (
    <>
      <div className="hidden lg:block fixed left-4 top-1/2 -translate-y-1/2 z-40 pointer-events-none">
        <motion.aside
          animate={expanded ? "expanded" : "collapsed"}
          variants={railVariants}
          onMouseEnter={() => finePointer && setExpanded(true)}
          onMouseLeave={() => finePointer && setExpanded(false)}
          className="pointer-events-auto glass-strong overflow-hidden border border-gold/12 rounded-[28px] shadow-[0_30px_90px_rgba(0,0,0,0.42)]"
        >
          <div className="relative">
            <div className="absolute inset-y-6 right-0 w-px bg-gradient-to-b from-transparent via-gold/20 to-transparent opacity-40" />
            <div className="absolute left-4 right-4 top-0 h-px bg-gradient-to-r from-transparent via-gold/55 to-transparent" />

            <div className="px-3 py-4">
              <div className="flex items-center justify-between gap-3 px-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="relative flex h-10 w-10 items-center justify-center rounded-full border border-gold/20">
                    <div className="absolute inset-1 rounded-full border border-white/10" />
                    <div className="h-1.5 w-1.5 rounded-full bg-gold shadow-[0_0_14px_rgba(221,167,79,0.65)]" />
                  </div>
                  <AnimatePresence>
                    {expanded ? (
                      <motion.div
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -8 }}
                        className="min-w-0"
                      >
                        <div className="font-serif text-lg leading-tight text-ink">Spiritual Profile Platform</div>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>

                <button
                  type="button"
                  onClick={() => setExpanded((value) => !value)}
                  className={`flex h-9 w-9 items-center justify-center rounded-full border transition-all ${
                    expanded
                      ? "border-gold/30 text-gold bg-gold/10"
                      : "border-white/10 text-ink-2 hover:text-gold hover:border-gold/20"
                  }`}
                  aria-label={expanded ? "Collapse side navigation" : "Expand side navigation"}
                >
                  <motion.div animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.28 }}>
                    <ChevronRight className="h-4 w-4" />
                  </motion.div>
                </button>
              </div>

              <div className="mt-5 space-y-1.5">
                {links.map((link, index) => renderLink(link, index))}
                <ThemeToggle theme={theme} toggleTheme={toggleTheme} expanded={expanded} />
              </div>

              <AnimatePresence>
                {expanded ? (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.24 }}
                    className="mt-6 border-t border-gold/10 pt-5 px-2"
                  >
                    <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-3">
                      PARTICIPANT ACTIVE
                    </div>
                    <div className="space-y-2">
                      <Link to="/dashboard" className="btn-gold w-full text-[16px] py-3 justify-start">
                        <UserRound className="h-4 w-4" /> Open Dashboard
                      </Link>
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          </div>
        </motion.aside>
      </div>

      {/* A fixed bar that is always reachable, plus a spacer of the same height in the page flow so it never
          covers a page heading. Inside the mobile app the app draws its own back arrow at the top-left,
          so the bar starts to the right of it. */}
      <div className="lg:hidden h-14" aria-hidden="true" />
      <div
        className={`lg:hidden fixed inset-x-0 top-0 z-40 flex h-14 items-center gap-3 border-b border-[color:var(--line)] bg-[color:var(--bg)] pr-4 ${
          embedded ? "pl-[72px]" : "pl-3"
        }`}
        data-testid="mobile-topbar"
      >
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[color:var(--line-gold)] text-gold"
          aria-label="Open side navigation"
        >
          <Menu className="h-5 w-5" />
        </button>
        <span className="truncate font-serif text-lg text-ink">Spiritual Profile Platform</span>
      </div>

      <AnimatePresence>
        {mobileOpen ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 z-50"
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={() => setMobileOpen(false)} />
            <motion.aside
              initial={{ x: -320 }}
              animate={{ x: 0 }}
              exit={{ x: -320 }}
              transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
              className="relative h-full w-[290px] glass-strong border-r border-gold/12 shadow-[0_30px_90px_rgba(0,0,0,0.45)]"
            >
              <div className={`px-4 pb-5 ${embedded ? "pt-20" : "pt-5"}`}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-serif text-2xl text-ink">Spiritual Profile Platform</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setMobileOpen(false)}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/20 text-gold"
                    aria-label="Close side navigation"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="mt-6 space-y-2">
                  {links.map((link, index) => renderLink(link, index, true))}
                  <ThemeToggle theme={theme} toggleTheme={toggleTheme} mobile />
                </div>

                <div className="mt-8 border-t border-gold/10 pt-5">
                  <div className="space-y-2">
                    <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="btn-gold w-full text-[16px] py-3 justify-start">
                      <UserRound className="h-4 w-4" /> Open Dashboard
                    </Link>
                  </div>
                </div>
              </div>
            </motion.aside>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
};

export default NavBar;
